<template>
  <AuthCard>
    <template #title>
      {{ t('auth.signup.title') }}
    </template>
    <template #caption>
      <div
        v-html="t('auth.signup.text', {
          terms: '/documents/terms',
          privacy: '/documents/privacy'
        })"
      ></div>
    </template>

    <template #form>
      <form
        @submit="onSubmit"
        :class="$style.form"
      >
        <UiField
          type="email"
          label="Email"
          name="email"
          placeholder="Email"
          :rounded="false"
        />

        <UiField
          type="password"
          :label="t('auth.signup.password')"
          name="password"
          :placeholder="t('auth.signup.password')"
          :rounded="false"
          :help="t('auth.signup.help')"
        />

        <UiSelect
          :default="defaultOption"
          :label="t('auth.signup.messenger')"
          placeholder="Choose messenger"
          name="messenger"
          :options="messengerOptions"
        />
        <UiField
          type="text"
          name="identifier"
          placeholder="@0detect_user1"
          :label="t('auth.signup.identifier')"
          :rounded="false"
          :help="t('auth.signup.bonus')"
        />

        <UiField
          type="text"
          name="refId"
          placeholder="Code"
          :rounded="false"
          :isDisabled="hasRefParam"
        >
          <template #label>
            <div v-html="t('auth.signup.referral')"></div>
          </template>
        </UiField>

        <UiCheckbox name="terms">
          <template #label>
            <div v-html="t('auth.signup.agree', {terms: '/documents/terms'})"></div>
          </template>
        </UiCheckbox>

        <UiButton :loading="loader">
          <template #label>
            {{ t('auth.signup.button') }}
          </template>
        </UiButton>
      </form>

      <div :class="$style.caption">
        <div v-html="t('auth.signup.account', {signin: '/auth/signin'})"></div>
      </div>
    </template>
  </AuthCard>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { filterPayload } from '@/utils/filterPayload.js';
import type { UserPayloadDto } from '~/dto';
import type { SelectOption } from '~/components/ui/Select';
import { countryLocator } from '~/utils/countryLocator';

const { t, locale } = useI18n();

useHead({
  title: t('meta.auth.signup.title'),
});

const router = useRouter();
const toastStore = useToastStore();
const { $localePath } = useNuxtApp();

const { $mixpanel } = useNuxtApp();
const isMobile = computed(() => window?.innerWidth > 768 ? 'desktop' : 'mobile');

const formData = ref({
  email: '',
  password: '',
  messenger: '',
  identifier: '',
  telegramUsername: '',
  refId: '',
  terms: false,
});

const messengerOptions = ref<SelectOption[]>([
  {
    label: 'Telegram',
    value: 'telegram',
  },
  {
    label: 'WhatsApp',
    value: 'whatsapp',
  },
  {
    label: 'WeChat',
    value: 'wechat',
  },
  {
    label: 'Line',
    value: 'line',
  },
])

const defaultOption = ref<string>('');

const schema: yup.AnyObject = yup.object({
  email: yup.string().email(t('errors.schema.email.invalid')).required(t('errors.schema.email.req')),
  password: yup
    .string()
    .min(8, t('errors.schema.pass'))
    .matches(/[0-9]/, t('errors.schema.number'))
    .matches(/[A-Z]/, t('errors.schema.upper'))
    .required(t('errors.schema.required')),
  messenger: yup.string(),
  identifier: locale.value !== 'en' ? yup.string().required(t('errors.schema.telegram')) : yup.string(),
  refId: yup.string(),
  terms: yup.bool()
    .oneOf([true], t('errors.schema.terms'))
    .required(t('errors.schema.terms.required')),
});

const loader = ref<boolean>(false);
const { handleSubmit, resetForm, setValues } = useForm({
  validationSchema: schema,
  initialValues: formData.value,
  validateOnMount: false,
});
const userPayloadKeys = ['email', 'password', 'refId', 'enabled2F'] as (keyof UserPayloadDto)[];
const contactsKeys = ['line', 'telegram', 'wechat', 'whatsapp']

const onSubmit = handleSubmit(
  async (values): Promise<void> => {
    try {
      loader.value = true;
      const payload = filterPayload(values, userPayloadKeys) as UserPayloadDto;

      const contacts: Record<string, any> = {};
      if (contactsKeys.includes(values.messenger)) {
        contacts[values.messenger] = values.identifier;
      }

      if (Object.keys(contacts).length > 0) {
        payload.contacts = contacts;
      }

      if (localStorage.getItem('gclid')) {
        payload.data = {
          gclid: localStorage.getItem('gclid'),
        }
      }

      if (localStorage.getItem('fbclid')) {
        payload.data = {
          fbclid: localStorage.getItem('fbclid'),
        };
      }

      const authApi = useNuxtApp().$authApi;
      const { result, data } = await authApi.signUpApi(payload);

      if (result) {
        $mixpanel.track('finish_registration', {
          name: data.email.split('@')[0],
          email: data.email,
          user_id: data._id,
          lang: window.navigator.language,
          messenger: {
            name: values.messenger,
            identifier: values.identifier,
          },
          utmSource: localStorage.getItem('utmSource'),
          utmMedium: localStorage.getItem('utmMedium'),
          utmCampaign: localStorage.getItem('utmCampaign'),
          utmContent: localStorage.getItem('utmContent'),
          utmTerm: localStorage.getItem('utmTerm'),
          refId: localStorage.getItem('refId'),
        })

        fbq('track', 'CompleteRegistration');
        dataLayer.push({ event: 'new_registration' });
        navigateTo({
          path: $localePath('/auth/confirm-account'),
          query: {
            email: data.email,
          },
        });
      } 
      if (data.statusCode === 409 && data.message === 'existEntity') {
        toastStore.showDanger({
          text: t('errors.signUp.existEntity'),
          duration: 3000,
        });
      }
    } catch (error) {
      console.error(error);
      toastStore.showDanger({
        text: t('errors.signUp.error'),
        duration: 3000,
      });
    } finally {
      loader.value = false;
    }
  }
);

const hasRefParam = computed(() => !!router.currentRoute.value.query?.refId);

onMounted(() => {
  const queryRefId = router.currentRoute.value.query?.refId;
  if (queryRefId) {
    setValues({
      ...formData.value,
      refId: queryRefId,
    },
      { validate: false }
    );
  } else {
    const storedRefId = localStorage.getItem('refId');
    if (storedRefId) {
      setValues({
        ...formData.value,
        refId: storedRefId,
      },
        { validate: false }
      );
    }
  }
});

onBeforeMount(() => {
  defaultOption.value = countryLocator();
})
</script>

<style lang="scss" module>
  .form {
    display: flex;
    flex-direction: column;
  }

  .caption {
    text-align: center;
    margin-top: 15px;

    a {
      color: #293EFF;
      text-decoration: none;
      transition: 200ms ease;

      &:hover {
        opacity: .8;
      }
    }
  }
</style>